import { cn } from '@/lib/utils'
import { ChevronDown } from '@/components/icons'
import { Pill } from '../ui/pill'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'

export type StepSectionBadge = 'required' | 'conditional' | 'optional'

const badgeLabels: Record<StepSectionBadge, string> = {
  required: 'Required',
  conditional: 'Conditional',
  optional: 'Optional',
}

// Numbered collapsible section card from the flight log redesign: round
// step-number chip + title on the left, status pill + chevron on the right.
export function StepSection({
  index,
  title,
  badge,
  open,
  onOpenChange,
  sectionKey,
  hasError,
  children,
}: {
  index: number
  title: string
  badge: StepSectionBadge
  open: boolean
  onOpenChange: (open: boolean) => void
  sectionKey: string
  // Failed submit validation — negative border + "Incomplete" pill
  hasError?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border bg-surface-L0',
        hasError ? 'border-border-negative-bold' : 'border-border-L2'
      )}
      id={`flight-log-section-${sectionKey}`}
    >
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger asChild>
          <button type="button" className="flex w-full items-center gap-2 p-3 text-left">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-L2 text-sm font-semibold text-content-secondary-bold">
              {index}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-content-primary-bold">
              {title}
            </span>
            <Pill variant={hasError ? 'error' : 'draft'}>
              {hasError ? 'Incomplete' : badgeLabels[badge]}
            </Pill>
            <ChevronDown
              className={cn(
                'size-4 shrink-0 text-content-primary-subtle transition-transform',
                open && 'rotate-180'
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col px-3 pb-4">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
